/**
 * アップロード経路A: ブラウザ側でのサムネイル生成。
 *
 * ARCHITECTURE 4章のとおり、画像のリサイズと動画のポスターフレーム抽出は
 * Canvas でクライアント側に行わせる。Worker も Container も通さない。
 *
 * 注意: **フル動画のトランスコードはここでは行わない**（CLAUDE.md 原則6）。
 * ここでやるのは「1フレームを静止画として切り出す」ことだけで、
 * デコードはブラウザの video 要素に任せている。
 */

export type Thumbnail = {
  blob: Blob
  width: number
  height: number
  contentType: 'image/jpeg'
  /** プレビュー表示用。使い終わったら revoke すること。 */
  objectUrl: string
}

const MAX_EDGE = 640
const JPEG_QUALITY = 0.82

function fitWithin(width: number, height: number, maxEdge: number) {
  const scale = Math.min(1, maxEdge / Math.max(width, height))
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

async function canvasToThumbnail(canvas: HTMLCanvasElement): Promise<Thumbnail> {
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  )
  if (!blob) throw new Error('サムネイルの生成に失敗しました')
  return {
    blob,
    width: canvas.width,
    height: canvas.height,
    contentType: 'image/jpeg',
    objectUrl: URL.createObjectURL(blob),
  }
}

/** 画像ファイルを縮小してサムネイルにする。 */
export async function thumbnailFromImage(file: File): Promise<Thumbnail> {
  const bitmap = await createImageBitmap(file)
  try {
    const size = fitWithin(bitmap.width, bitmap.height, MAX_EDGE)
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas コンテキストを取得できません')
    ctx.drawImage(bitmap, 0, 0, size.width, size.height)
    return await canvasToThumbnail(canvas)
  } finally {
    bitmap.close()
  }
}

/**
 * フレームが「のっぺり」しているか（真っ黒・真っ白・単色）を判定する。
 *
 * 動画の冒頭は黒みで始まることが多く、そのまま掴むとサムネイルが真っ黒になる。
 * 平均輝度だけだと灰色一色を見逃すので、輝度の標準偏差も見る。
 *
 * @param rgba Canvas の getImageData().data
 */
export function isBlankFrame(rgba: Uint8ClampedArray): boolean {
  const pixels = rgba.length / 4
  if (pixels === 0) return true

  // 全画素見る必要はない。等間隔にサンプリングする。
  const step = Math.max(1, Math.floor(pixels / 2000))
  let sum = 0
  let sumSq = 0
  let count = 0

  for (let i = 0; i < pixels; i += step) {
    const o = i * 4
    // ITU-R BT.601 の輝度
    const y = 0.299 * rgba[o]! + 0.587 * rgba[o + 1]! + 0.114 * rgba[o + 2]!
    sum += y
    sumSq += y * y
    count += 1
  }

  const mean = sum / count
  const variance = Math.max(0, sumSq / count - mean * mean)
  const stdDev = Math.sqrt(variance)

  // ほぼ変化が無い（単色）なら中身のないフレームとみなす
  if (stdDev < 6) return true
  // 変化があっても極端に暗ければ使い物にならない
  if (mean < 8) return true
  return false
}

/** シーク後、実際にフレームが描画されるまで待つ。 */
async function waitForFrame(video: HTMLVideoElement): Promise<void> {
  // Safari / Chrome とも、seeked の直後は前のフレームが残っていることがある。
  // requestVideoFrameCallback があれば「表示された」ことを確実に待てる。
  const withCallback = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: () => void) => number
  }
  if (typeof withCallback.requestVideoFrameCallback === 'function') {
    await new Promise<void>((resolve) => {
      withCallback.requestVideoFrameCallback!(() => resolve())
      // 稀にコールバックが来ないので保険を張る
      setTimeout(resolve, 500)
    })
    return
  }
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
}

async function seekTo(video: HTMLVideoElement, seconds: number): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked)
      resolve()
    }
    const onError = () => {
      video.removeEventListener('error', onError)
      reject(new Error('シークに失敗しました'))
    }
    video.addEventListener('seeked', onSeeked)
    video.addEventListener('error', onError)
    video.currentTime = seconds
  })
  await waitForFrame(video)
}

/**
 * 動画から代表的なフレームを1枚抜き出す。
 *
 * 冒頭の黒みを掴まないよう、複数の位置を試して「中身のあるフレーム」を選ぶ。
 * ブラウザがデコードできるコーデックである必要がある。ProRes などの
 * 業務用コーデックは再生できないことがあるので、失敗を握りつぶさず呼び出し側へ返す。
 */
export async function thumbnailFromVideo(
  file: File,
  options: { atSeconds?: number } = {},
): Promise<Thumbnail> {
  const url = URL.createObjectURL(file)
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'
  video.src = url

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve()
      video.onerror = () =>
        reject(new Error('この動画はブラウザで再生できないため、ポスターフレームを抽出できません'))
    })

    const duration = Number.isFinite(video.duration) ? video.duration : 0
    // 明示指定があればそれだけ。無ければ黒みを避けるため複数点を試す。
    const candidates =
      options.atSeconds != null
        ? [options.atSeconds]
        : [0.1, 0.25, 0.5, 0.75, 0.02].map((r) =>
            Math.min(Math.max(duration * r, 0.1), Math.max(duration - 0.05, 0.1)),
          )

    const size = fitWithin(video.videoWidth, video.videoHeight, MAX_EDGE)
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new Error('canvas コンテキストを取得できません')

    let lastDrawn = false
    for (const t of candidates) {
      await seekTo(video, t)
      ctx.drawImage(video, 0, 0, size.width, size.height)
      lastDrawn = true
      const frame = ctx.getImageData(0, 0, size.width, size.height)
      if (!isBlankFrame(frame.data)) {
        return await canvasToThumbnail(canvas)
      }
    }

    // どこを見ても単色だった（本当に真っ黒な映像など）。最後に描いたものを返す。
    if (!lastDrawn) throw new Error('フレームを取得できませんでした')
    return await canvasToThumbnail(canvas)
  } finally {
    video.src = ''
    URL.revokeObjectURL(url)
  }
}

/** 拡張子ではなく MIME で振り分ける。 */
export async function generateThumbnail(file: File): Promise<Thumbnail> {
  if (file.type.startsWith('image/')) return thumbnailFromImage(file)
  if (file.type.startsWith('video/')) return thumbnailFromVideo(file)
  throw new Error(`サムネイルを生成できない形式です: ${file.type || '不明'}`)
}

/** 動画の長さ・解像度だけを取る（素材メタデータの補完用）。 */
export async function readVideoMetadata(
  file: File,
): Promise<{ durationMs: number; width: number; height: number } | null> {
  const url = URL.createObjectURL(file)
  const video = document.createElement('video')
  video.preload = 'metadata'
  video.src = url
  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve()
      video.onerror = () => reject(new Error('メタデータを読めません'))
    })
    return {
      durationMs: Math.round(video.duration * 1000),
      width: video.videoWidth,
      height: video.videoHeight,
    }
  } catch {
    return null
  } finally {
    video.src = ''
    URL.revokeObjectURL(url)
  }
}
